import {RouteNames} from '@/brief-hub/router/route-names';
import {NavigationGuard, RouteLocationNormalized, RouteRecordName} from 'vue-router';
import {StrategyDraftState, StrategyDraftStateWrapper} from '@/common/api/api-raw-types';
import {useBriefStrategyStore} from '@/brief-hub/stores/briefStrategy.store';
import {computed} from 'vue';

export interface NavigationStrategyService {
    nextIncompleteRoute(draftStateWrapper: StrategyDraftStateWrapper): string;
    nextRoute(routeName: RouteRecordName | null | undefined): string;
    previousRoute(routeName: string): string | null;
    requireDraftState(requiredDraftState: StrategyDraftState): NavigationGuard;
    isRouteAfter(route1: string, route2: string): boolean;
    isRequiredDraftStateCompleted(draftStateWrapper: StrategyDraftStateWrapper, requiredDraftState: StrategyDraftState): boolean;
    requireRedirectionReason(): NavigationGuard;
}

export function useNavigationStrategyService(): NavigationStrategyService {
    const navigation: string[] = [
        RouteNames.STRATEGY_BRIEF_FREELANCER_PROFILE,
        RouteNames.STRATEGY_BRIEF_FREELANCER_FUNCTIONAL_SCOPES,
        RouteNames.STRATEGY_BRIEF_FREELANCER_INDUSTRY_EXPERTISES,
        RouteNames.STRATEGY_BRIEF_FREELANCER_SKILLS,
        RouteNames.STRATEGY_BRIEF_WHEN_CONDITIONS,
        RouteNames.STRATEGY_BRIEF_WHERE_CONDITIONS,
        RouteNames.STRATEGY_BRIEF_MISSION_DESCRIPTION,
        RouteNames.STRATEGY_BRIEF_OVERVIEW,
    ];

    const nextRouteFrom: Partial<Record<StrategyDraftState, string>> = {
        INITIALIZED: RouteNames.STRATEGY_BRIEF_FREELANCER_PROFILE,
        FREELANCER_SECTION_COMPLETED: RouteNames.STRATEGY_BRIEF_FREELANCER_FUNCTIONAL_SCOPES,
        FREELANCER_FUNCTIONAL_SCOPES_SECTION_COMPLETED: RouteNames.STRATEGY_BRIEF_FREELANCER_INDUSTRY_EXPERTISES,
        FREELANCER_INDUSTRY_EXPERTISES_SECTION_COMPLETED: RouteNames.STRATEGY_BRIEF_FREELANCER_SKILLS,
        FREELANCER_SKILLS_SECTION_COMPLETED: RouteNames.STRATEGY_BRIEF_WHEN_CONDITIONS,
        WHEN_CONDITIONS_SECTION_COMPLETED: RouteNames.STRATEGY_BRIEF_WHERE_CONDITIONS,
        WHERE_CONDITIONS_SECTION_COMPLETED: RouteNames.STRATEGY_BRIEF_MISSION_DESCRIPTION,
        MISSION_DESCRIPTION_SECTION_COMPLETED: RouteNames.STRATEGY_BRIEF_OVERVIEW,
    };

    function requireDraftState(requiredDraftState: StrategyDraftState): NavigationGuard {
        return async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: (route?: Partial<RouteLocationNormalized>) => void) => {
            const briefStrategyStore = useBriefStrategyStore();

            await getBriefIfNeeded(to.params.id as string);

            const brief = computed(() => briefStrategyStore.brief);

            if (!brief || !brief.value?.draftState) {
                next({name: RouteNames.NOT_FOUND});
            } else if (isRequiredDraftStateCompleted(brief.value.draftState as StrategyDraftStateWrapper, requiredDraftState)) {
                next();
            } else {
                const routeName = nextIncompleteRoute(brief.value.draftState as StrategyDraftStateWrapper);
                next({name: routeName, params: to.params, query: to.query});
            }
        };
    }

    function isRequiredDraftStateCompleted(draftStateWrapper: StrategyDraftStateWrapper, requiredDraftState: StrategyDraftState): boolean {
        const draftStateList = Object.keys(nextRouteFrom);

        const completed = draftStateList.indexOf(draftStateWrapper.strategyDraftState);
        const required = draftStateList.indexOf(requiredDraftState);

        return completed >= required;
    }

    function nextIncompleteRoute(draftStateWrapper: StrategyDraftStateWrapper): string {
        return nextRouteFrom[draftStateWrapper.strategyDraftState] || RouteNames.NOT_FOUND;
    }

    function nextRoute(routeName: RouteRecordName | null | undefined): string {
        if (typeof routeName === 'string') {
            const currentRouteIndex = navigation.indexOf(routeName);
            if (currentRouteIndex < navigation.length) {
                return navigation[currentRouteIndex + 1];
            }
        }
        return RouteNames.NOT_FOUND;
    }

    function previousRoute(routeName: string): string | null {
        const currentRouteIndex = navigation.indexOf(routeName);
        if (currentRouteIndex > 0) {
            return navigation[currentRouteIndex - 1];
        }
        return null;
    }

    function isRouteAfter(route1: string, route2: string): boolean {
        const route1Index = navigation.indexOf(route1);
        const route2Index = navigation.indexOf(route2);
        return route1Index > route2Index;
    }

    async function getBriefIfNeeded(briefId: string): Promise<void> {
        const briefStrategyStore = useBriefStrategyStore();

        const brief = computed(() => briefStrategyStore.brief);
        if (!brief.value || brief.value.briefId !== briefId) {
            await briefStrategyStore.getStrategyBrief(briefId);
        }
    }

    function requireRedirectionReason(): NavigationGuard {
        return async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: (route?: Partial<RouteLocationNormalized>) => void) => {
            const briefStrategyStore = useBriefStrategyStore();
            if (briefStrategyStore.redirectionReason != null) {
                next();
            } else {
                next({name: RouteNames.NOT_FOUND});
            }
        };
    }

    return {
        nextIncompleteRoute,
        nextRoute,
        previousRoute,
        requireDraftState,
        isRouteAfter,
        isRequiredDraftStateCompleted,
        requireRedirectionReason,
    };
}
